/**
 * ContactDetailModalMobile
 * ─────────────────────────
 * Mobile-only contact detail sheet.
 * Mirrors ContactDetailPanel but uses the DemandDetailModalMobile
 * visual language: Overlay + Sheet + Nav + BottomSheet pickers.
 *
 * Props:
 *   isOpen     – controls visibility
 *   contatoId  – id of the contact to load (fetches internally)
 *   onClose    – called when the user taps the back button
 */

import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import TagSelector, { Tag } from './TagSelector';
import { DemandDetailModal } from './DemandDetailModal';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';
import citiesData from '../../data/cities_with_coordinates.json';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CityData {
  id: number;
  nome: string;
  microrregiao: { mesorregiao: { UF: { sigla: string } } };
}

interface Eleitor {
  id: number;
  nome: string;
  telefone: string | null;
  email: string | null;
  cidade_id: number | null;
  data_nascimento: string | null;
  profissao: string | null;
  time_coracao: string | null;
  rede_social: string | null;
  observacoes: string | null;
  multiplicador?: boolean;
  cidade?: { id: number; nome: string; uf: string } | null;
  assessor?: { id: number; nome: string } | null;
  tags?: Tag[];
  data_cadastro?: string | null;
}

interface Registro {
  id: number;
  descricao: string;
  created_at: string;
  updated_at: string;
  usuario: { id: number; nome: string };
}

interface LogEntry {
  id: number;
  data_hora: string;
  acao: string;
  observacao: string;
  registrou: { id: number; nome: string } | null;
}

type SheetKind = 'cidade' | 'time' | 'nome' | 'telefone' | 'email' | 'nascimento' | 'profissao' | 'rede_social' | 'observacoes';

// ─── Constants ────────────────────────────────────────────────────────────────

const timesRS = [
  'Grêmio', 'Internacional', 'Juventude', 'Caxias', 'Brasil de Pelotas',
  'Ypiranga', 'São José (POA)', 'Esportivo', 'Aimoré', 'São Luiz',
  'Guarany de Bagé', 'Outros',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatPhone = (phone: string | null): string => {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 13) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
};

const fmtDate = (d: string | null | undefined) => {
  if (!d) return null;
  const parts = d.slice(0, 10).split('-');
  if (parts.length !== 3) return d;
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
    .toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const calcAge = (d: string | null | undefined): number | null => {
  if (!d) return null;
  const parts = d.slice(0, 10).split('-');
  const birth = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  const today = new Date();
  return today.getFullYear() - birth.getFullYear()
    - (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
};

const isBirthdayToday = (d: string | null | undefined): boolean => {
  if (!d) return false;
  const parts = d.slice(0, 10).split('-');
  const birth = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  const today = new Date();
  return birth.getDate() === today.getDate() && birth.getMonth() === today.getMonth();
};

const ago = (d: string) => {
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'ontem';
  if (days < 7) return `${days}d`;
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

// ─── Animations ───────────────────────────────────────────────────────────────

const shimmer = keyframes`
  from { background-position: -200% 0; }
  to   { background-position:  200% 0; }
`;
const shimmerBg = css`
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
  border-radius: 6px;
`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

// ─── Overlay & Sheet ──────────────────────────────────────────────────────────

const Overlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 3200;
    flex-direction: column;
    opacity: ${p => (p.$open ? 1 : 0)};
    pointer-events: ${p => (p.$open ? 'auto' : 'none')};
    transition: opacity 0.24s ease;
  }
`;

const Sheet = styled.div<{ $open: boolean }>`
  background: #f8fafc;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${p => (p.$open ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

// ─── Nav ──────────────────────────────────────────────────────────────────────

const Nav = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 12px 8px 10px;
  background: rgba(248, 250, 252, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const NavIconBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(120, 120, 128, 0.12);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007aff;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
  &:active { background: rgba(120, 120, 128, 0.24); }
  svg { width: 18px; height: 18px; }
`;

const NavCenter = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 6px;
  overflow: hidden;
`;

const NavSup = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1;
`;

const NavTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Body ─────────────────────────────────────────────────────────────────────

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 90px;
`;

// ─── Birthday Banner ──────────────────────────────────────────────────────────

const BirthdayBanner = styled.div`
  background: linear-gradient(100deg, #fff8f0 60%, #ffe4d6 100%);
  border-bottom: 1px solid #fde4cc;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 14px 14px 0 0;
`;

const BirthdayCakeIcon = styled.div`
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 10px;
  background: #fff3e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const BirthdayText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const BirthdayTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.07em;
  color: #c2410c;
  text-transform: uppercase;
`;

const BirthdayDesc = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c1e;
`;

// ─── Hero Card ────────────────────────────────────────────────────────────────

const HeroCard = styled.div`
  background: #fff;
  border-radius: 18px;
  margin: 12px 12px 0;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const HeroCardInner = styled.div`
  padding: 20px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroDivider = styled.div`
  width: 100%;
  height: 1px;
  border-top: 1.5px dashed #e2e8f0;
  margin: 14px 0;
`;

const CreatorPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 12px 0;
  padding: 8px 14px 8px 8px;
  background: #fff;
  border: 1px solid #e9edf2;
  border-radius: 999px;
  width: fit-content;
`;

const CreatorAvatar = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f97316, #fb923c);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
`;

const CreatorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CreatorLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const CreatorName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1c1c1e;
`;

// ─── Avatar Hero ──────────────────────────────────────────────────────────────

const AvatarHeroWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const AvatarRing = styled.div<{ $ring?: boolean }>`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  padding: 3px;
  background: ${p => p.$ring ? 'linear-gradient(135deg, #f97316, #fb923c)' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  position: relative;
  letter-spacing: -1px;
  border: 3px solid #fff;
`;

const MultiplicadorStar = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f59e0b;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const HeroName = styled.div<{ $editable?: boolean }>`
  margin-top: 10px;
  font-size: 22px;
  font-weight: 700;
  color: #000;
  letter-spacing: -0.03em;
  text-align: center;
  cursor: ${p => p.$editable ? 'pointer' : 'default'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
  &:active { opacity: ${p => p.$editable ? 0.6 : 1}; }
`;

const HeroBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 6px;
`;

const HeroBadge = styled.span<{ $variant?: 'default' | 'gold' | 'blue' | 'city' }>`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${p => {
    if (p.$variant === 'gold') return 'background:#fef3c7;color:#92400e; border: 1.5px solid #fde68a;';
    if (p.$variant === 'blue') return 'background:#eff6ff;color:#1d4ed8;';
    if (p.$variant === 'city') return 'background:#f1f5f9;color:#475569; border: 1px solid #e2e8f0;';
    return 'background:#f1f5f9;color:#475569;';
  }}
`;

const HeroActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
`;

const HeroActionBtn = styled.button<{ $color: string; $bg: string }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 13px 14px;
  border-radius: 14px;
  border: none;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.15s;
  &:active { filter: brightness(0.95); }
  svg { width: 18px; height: 18px; flex-shrink: 0; }
`;

// ─── Tags row ─────────────────────────────────────────────────────────────────

const TagsCard = styled.div`
  background: #fff;
  border-radius: 14px;
  margin: 8px 16px 0;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

const TagsCardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

// ─── Info Cards ───────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  margin: 10px 16px 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

const CardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 12px 14px 4px;
`;

const Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px 14px;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f2f2f7;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  gap: 12px;
  &:last-child { border-bottom: none; }
  &:active { background: #f9fafb; }
`;

const RowIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #64748b;
  svg { width: 15px; height: 15px; }
`;

const RowContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const RowLabel = styled.div`
  font-size: 12px;
  color: #8e8e93;
  font-weight: 500;
`;

const RowValue = styled.div`
  font-size: 15px;
  color: #000;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
`;

const RowEmpty = styled(RowValue)`
  color: #c7c7cc;
  font-style: italic;
`;

const RowChevron = styled.div`
  color: #c7c7cc;
  flex-shrink: 0;
  svg { width: 14px; height: 14px; }
`;

// ─── Notas card ───────────────────────────────────────────────────────────────

const NotasCard = styled(Card)``;

const NotasBody = styled.div`
  padding: 12px 14px;
`;

const NotasText = styled.div`
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const NotasEmpty = styled.div`
  font-size: 14px;
  color: #c7c7cc;
  font-style: italic;
`;

const NotasEditBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  background: none;
  border: none;
  color: #007aff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

// ─── Activity Tabs ────────────────────────────────────────────────────────────

const ActivityCard = styled(Card)``;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid #f2f2f7;
`;

const Tab = styled.button<{ $a?: boolean }>`
  flex: 1;
  padding: 12px 6px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: ${p => p.$a ? 700 : 500};
  color: ${p => p.$a ? '#007aff' : '#8e8e93'};
  cursor: pointer;
  border-bottom: 2px solid ${p => p.$a ? '#007aff' : 'transparent'};
  margin-bottom: -1px;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const TabBadge = styled.span`
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 999px;
`;

const FeedWrap = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const DateGroup = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 10px 0 6px;
`;

const FeedItem = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const FeedLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 28px;
`;

const FeedAvatar = styled.div<{ $type: 'comment' | 'history' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => p.$type === 'comment' ? '#e8eaf6' : '#f0fdf4'};
  color: ${p => p.$type === 'comment' ? '#5c6bc0' : '#16a34a'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { width: 12px; height: 12px; }
`;

const FeedLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 12px;
  background: #e9edf2;
  margin: 3px 0;
`;

const FeedContent = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: 10px;
  padding-top: 3px;
`;

const FeedMeta = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: #374151;
`;

const FeedAuthor = styled.span`
  font-weight: 700;
  color: #111827;
`;

const FeedAction = styled.span`
  color: #6b7280;
`;

const FeedTime = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-left: 4px;
`;

const FeedCard = styled.div`
  margin-top: 5px;
  background: #f8fafc;
  border: 1px solid #e9edf2;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Empty = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 14px;
  color: #c7c7cc;
`;

// ─── Demanda item ─────────────────────────────────────────────────────────────

const DemandaRow = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #f2f2f7;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  &:last-child { border-bottom: none; }
`;

const DemandaTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  flex: 1;
  min-width: 0;
`;

const DemandaStatus = styled.span<{ $status: string }>`
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  ${p => {
    switch (p.$status) {
      case 'NAO_INICIADA': return 'background:#dbeafe;color:#1e40af;';
      case 'EM_ANDAMENTO': return 'background:#fef3c7;color:#92400e;';
      case 'PAUSADA':      return 'background:#fef3c7;color:#92400e;';
      case 'CONCLUIDA':    return 'background:#d1fae5;color:#065f46;';
      case 'CANCELADA':    return 'background:#fee2e2;color:#991b1b;';
      default:             return 'background:#f1f5f9;color:#64748b;';
    }
  }}
`;

const DemandaStatusLabel: Record<string, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

// ─── Composer Bar ─────────────────────────────────────────────────────────────

const ComposerBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3300;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  padding: 10px 12px;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  display: none;
  @media (max-width: 900px) { display: flex; }
  align-items: flex-end;
  gap: 8px;
`;

const ComposerInput = styled.textarea`
  flex: 1;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 20px;
  padding: 9px 14px;
  font-size: 14px;
  color: #0f172a;
  font-family: inherit;
  resize: none;
  outline: none;
  max-height: 100px;
  line-height: 1.45;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const ComposerSend = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #007aff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:disabled { background: #e2e8f0; }
  svg { width: 16px; height: 16px; color: #fff; }
`;

// ─── Bottom Sheet ─────────────────────────────────────────────────────────────

const SheetOverlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 4000;
    background: rgba(0,0,0,${p => p.$open ? 0.4 : 0});
    pointer-events: ${p => p.$open ? 'auto' : 'none'};
    transition: background 0.24s;
  }
`;

const BottomSheet = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 4100;
    background: #fff;
    border-radius: 20px 20px 0 0;
    max-height: 80vh;
    transform: ${p => p.$open ? 'translateY(0)' : 'translateY(100%)'};
    transition: transform 0.3s cubic-bezier(0.2,0.8,0.2,1);
  }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 10px auto 0;
`;

const SheetTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  padding: 14px 18px 10px;
  border-bottom: 1px solid #f1f5f9;
`;

const SheetList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const SheetOption = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 13px 18px;
  background: ${p => p.$active ? 'rgba(var(--accent-rgb), 0.08)' : 'transparent'};
  border: none;
  text-align: left;
  font-size: 15px;
  color: ${p => p.$active ? 'var(--accent)' : '#0f172a'};
  font-weight: ${p => p.$active ? 700 : 400};
  cursor: pointer;
  border-bottom: 1px solid #f8fafc;
  -webkit-tap-highlight-color: transparent;
  &:active { background: #f1f5f9; }
`;

const SheetInput = styled.input`
  margin: 10px 16px;
  padding: 10px 12px;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  width: calc(100% - 32px);
  box-sizing: border-box;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const SheetTextArea = styled.textarea`
  margin: 10px 16px;
  padding: 10px 12px;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  width: calc(100% - 32px);
  box-sizing: border-box;
  resize: none;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.5;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const SheetDateInput = styled.input`
  margin: 10px 16px;
  padding: 10px 12px;
  background: #f1f5f9;
  border: 1.5px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  width: calc(100% - 32px);
  box-sizing: border-box;
  &:focus { border-color: rgba(var(--accent-rgb), 0.4); background: #fff; }
`;

const SheetActions = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
`;

const SheetBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${p => p.$primary ? '#007aff' : '#e2e8f0'};
  background: ${p => p.$primary ? '#007aff' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#374151'};
  &:disabled { opacity: 0.5; }
`;

// ─── Shimmer ──────────────────────────────────────────────────────────────────

const ShimmerLine = styled.div<{ $w: string | number; $h: number }>`
  ${shimmerBg}
  width: ${p => typeof p.$w === 'number' ? `${p.$w}px` : p.$w};
  height: ${p => p.$h}px;
`;

const Spinner = styled.div`
  width: 16px; height: 16px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: #007aff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  display: inline-block;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export interface ContactDetailModalMobileProps {
  isOpen: boolean;
  contatoId: number | null;
  onClose: () => void;
}

const ContactDetailModalMobile: React.FC<ContactDetailModalMobileProps> = ({
  isOpen,
  contatoId,
  onClose,
}) => {
  const { session, userData } = useAuth();
  const { can, loading: permissionsLoading } = usePermissionCheck();

  const [contact, setContact] = useState<Eleitor | null>(null);
  const [loadingContact, setLoadingContact] = useState(false);

  const [registros, setRegistros] = useState<Registro[]>([]);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [loadingRegistros, setLoadingRegistros] = useState(false);

  const [demandas, setDemandas] = useState<any[]>([]);
  const [loadingDemandas, setLoadingDemandas] = useState(false);
  const [openDemand, setOpenDemand] = useState<any | null>(null);

  const [atab, setAtab] = useState<'registros' | 'demandas'>('registros');

  const [newRegistro, setNewRegistro] = useState('');
  const [sendingRegistro, setSendingRegistro] = useState(false);

  const [savingField, setSavingField] = useState<string | null>(null);

  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  // Bottom sheet state
  const [sheetKind, setSheetKind] = useState<SheetKind | null>(null);
  const [sheetValue, setSheetValue] = useState('');
  const [sheetSearch, setSheetSearch] = useState('');
  const [savingSheet, setSavingSheet] = useState(false);

  const contactRef = useRef<Eleitor | null>(null);
  contactRef.current = contact;

  const cities = (citiesData as CityData[]).filter(c => c.microrregiao?.mesorregiao?.UF?.sigla === 'RS');
  const sortedCities = [...cities].sort((a, b) => a.nome.localeCompare(b.nome));

  const canEditContact = !permissionsLoading && (
    can('contacts.edit.all') ||
    (can('contacts.edit.own') && !!contact && (contact as any).assessor?.id === userData?.id)
  );

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Fetch on open
  useEffect(() => {
    if (!isOpen || !contatoId || !session?.access_token) return;
    setContact(null);
    setRegistros([]);
    setLogEntries([]);
    setDemandas([]);
    setAtab('registros');
    setNewRegistro('');
    fetchContact(contatoId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contatoId, session?.access_token]);

  const fetchContact = async (id: number) => {
    setLoadingContact(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${id}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const c: Eleitor = data.data ?? data;
        setContact(c);
        setSelectedTags(c.tags || []);
        fetchRegistros(c.id);
        fetchDemandas(c.id);
      }
    } catch (e) {
      console.error('ContactDetailModalMobile: fetchContact error', e);
    } finally {
      setLoadingContact(false);
    }
  };

  const fetchRegistros = async (eleitorId: number) => {
    setLoadingRegistros(true);
    try {
      const [regRes, logRes] = await Promise.all([
        apiFetch(`${API_BASE_URL}/api/v1/eleitores/${eleitorId}/registros`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        }),
        apiFetch(`${API_BASE_URL}/api/v1/eleitores/${eleitorId}/historico`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        }),
      ]);
      if (regRes.ok) { const d = await regRes.json(); setRegistros(d.data || []); }
      if (logRes.ok) { const d = await logRes.json(); setLogEntries(d.data || []); }
    } catch {}
    finally { setLoadingRegistros(false); }
  };

  const fetchDemandas = async (eleitorId: number) => {
    setLoadingDemandas(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/demandas?solicitante_id=${eleitorId}`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (res.ok) { const d = await res.json(); setDemandas(d.data || []); }
    } catch {}
    finally { setLoadingDemandas(false); }
  };

  const saveField = async (fieldKey: string, body: Record<string, any>) => {
    const c = contactRef.current;
    if (!c || !session?.access_token) return;
    setSavingField(fieldKey);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Erro ao atualizar');
      const data = await res.json();
      const serverContact = data.data as Eleitor;
      setContact({ ...serverContact, tags: contactRef.current?.tags });
      fetchRegistros(c.id);
    } catch (e) {
      console.error('saveField error', e);
      alert('Erro ao salvar alteração');
    } finally {
      setSavingField(null);
    }
  };

  const saveContactTags = async (tags: Tag[]) => {
    const c = contactRef.current;
    if (!c || !session?.access_token) return;
    setSelectedTags(tags);
    setSavingField('tags');
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ tag_ids: tags.map(t => t.id) }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setContact({ ...(data.data as Eleitor), tags });
    } catch {
      setSelectedTags(c.tags || []);
    } finally {
      setSavingField(null);
    }
  };

  const handleAddRegistro = async () => {
    const c = contactRef.current;
    if (!c?.id || !newRegistro.trim() || !session?.access_token) return;
    setSendingRegistro(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/v1/eleitores/${c.id}/registros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ descricao: newRegistro.trim() }),
      });
      if (res.ok) { setNewRegistro(''); fetchRegistros(c.id); }
    } catch {}
    finally { setSendingRegistro(false); }
  };

  // Sheet helpers
  const openSheet = (kind: SheetKind, currentValue = '') => {
    setSheetKind(kind);
    setSheetValue(currentValue);
    setSheetSearch('');
  };
  const closeSheet = () => { setSheetKind(null); setSheetValue(''); setSheetSearch(''); };

  const saveSheet = async () => {
    const c = contactRef.current;
    if (!c || savingSheet) return;
    setSavingSheet(true);
    try {
      switch (sheetKind) {
        case 'nome':        await saveField('nome', { nome: sheetValue }); break;
        case 'telefone':    await saveField('telefone', { telefone: sheetValue || null }); break;
        case 'email':       await saveField('email', { email: sheetValue || null }); break;
        case 'profissao':   await saveField('profissao', { profissao: sheetValue || null }); break;
        case 'rede_social': await saveField('rede_social', { rede_social: sheetValue || null }); break;
        case 'observacoes': await saveField('observacoes', { observacoes: sheetValue || null }); break;
        case 'nascimento':  await saveField('data_nascimento', { data_nascimento: sheetValue || null }); break;
      }
      closeSheet();
    } finally {
      setSavingSheet(false);
    }
  };

  const saveSheetCity = async (cityId: number) => {
    closeSheet();
    await saveField('cidade_id', { cidade_id: cityId });
  };

  const saveSheetTime = async (time: string) => {
    closeSheet();
    await saveField('time_coracao', { time_coracao: time });
  };

  // Timeline builder
  type CommentItem = Registro & { _source: 'comment' };
  type HistoryItem = LogEntry & { _source: 'history'; created_at: string };
  type TimelineEntry = CommentItem | HistoryItem;

  const buildTimeline = (): { label: string; items: TimelineEntry[] }[] => {
    const all: TimelineEntry[] = [
      ...registros.map(r => ({ ...r, _source: 'comment' as const })),
      ...logEntries.map(l => ({ ...l, _source: 'history' as const, created_at: l.data_hora })),
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const groups: { label: string; items: TimelineEntry[] }[] = [];
    const seen = new Map<string, number>();

    all.forEach(r => {
      const d = new Date(r.created_at); d.setHours(0, 0, 0, 0);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const key = `${d.getFullYear()}-${mm}-${dd}`;
      let label = `${dd}/${mm}`;
      if (d.getTime() === today.getTime()) label = `HOJE · ${dd}/${mm}`;
      else if (d.getTime() === yesterday.getTime()) label = `ONTEM · ${dd}/${mm}`;
      if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ label, items: [] }); }
      groups[seen.get(key)!].items.push(r);
    });

    return groups;
  };

  const initials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const canCreate = can('contacts.comments.create') || can('contacts.comments.create.own');
  const birthdayToday = contact ? isBirthdayToday(contact.data_nascimento) : false;

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────

  const timelineGroups = buildTimeline();

  return (
    <>
      <Overlay $open={isOpen}>
        <Sheet $open={isOpen}>
          {/* ── Nav ── */}
          <Nav>
            <NavIconBtn onClick={onClose} aria-label="Voltar">
              <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavIconBtn>
            <NavCenter>
              <NavTitle>Contato</NavTitle>
            </NavCenter>
            <NavIconBtn
              aria-label="Opções"
              onClick={() => {
                if (!contact) return;
                if (contact.multiplicador !== undefined) {
                  saveField('multiplicador', { multiplicador: !contact.multiplicador });
                }
              }}
              style={{ background: 'transparent' }}
            >
              <svg fill="currentColor" viewBox="0 0 24 24" width="20" height="20">
                <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
            </NavIconBtn>
          </Nav>

          {/* ── Body ── */}
          <Body>
              {loadingContact || !contact ? (
              <div style={{ padding: '32px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <ShimmerLine $w={80} $h={80} style={{ borderRadius: '50%' }} />
                <ShimmerLine $w="60%" $h={22} style={{ marginTop: 8 }} />
                <ShimmerLine $w="40%" $h={14} />
                <ShimmerLine $w="100%" $h={14} style={{ marginTop: 16 }} />
                <ShimmerLine $w="80%" $h={14} />
              </div>
            ) : (
              <>
                {/* ── Hero Card ── */}
                <HeroCard>
                  {birthdayToday && (
                    <BirthdayBanner>
                      <BirthdayCakeIcon>🎂</BirthdayCakeIcon>
                      <BirthdayText>
                        <BirthdayTitle>Aniversário Hoje</BirthdayTitle>
                        <BirthdayDesc>
                          Faz {calcAge(contact.data_nascimento)} anos · {new Date(contact.data_nascimento! + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                        </BirthdayDesc>
                      </BirthdayText>
                    </BirthdayBanner>
                  )}
                  <HeroCardInner>
                    <AvatarHeroWrap>
                      <AvatarRing $ring={!!contact.multiplicador}>
                        <AvatarCircle>
                          {initials(contact.nome)}
                          {contact.multiplicador && <MultiplicadorStar>★</MultiplicadorStar>}
                        </AvatarCircle>
                      </AvatarRing>
                      <HeroName $editable={canEditContact} onClick={() => canEditContact && openSheet('nome', contact.nome)}>
                        {contact.nome}
                        {canEditContact && (
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14" style={{ color: '#94a3b8', flexShrink: 0 }}>
                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </HeroName>
                      {contact.profissao && (
                        <div style={{ fontSize: 14, color: '#64748b', marginTop: 2, textAlign: 'center' }}>{contact.profissao}</div>
                      )}
                      <HeroBadges>
                        {contact.cidade && (
                          <HeroBadge $variant="city">
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="11" height="11"><path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            {contact.cidade.nome}
                          </HeroBadge>
                        )}
                        {contact.multiplicador && (
                          <HeroBadge $variant="gold">★ Multiplicador</HeroBadge>
                        )}
                      </HeroBadges>
                      {contact.telefone && (
                        <>
                          <HeroDivider />
                          <HeroActions style={{ width: '100%' }}>
                            <HeroActionBtn
                              $color="#1a9c4e"
                              $bg="#e8f5ee"
                              onClick={() => window.open(`https://wa.me/${contact.telefone!.replace(/\D/g, '')}`, '_blank')}
                            >
                              <svg fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                              </svg>
                              WhatsApp
                            </HeroActionBtn>
                            <HeroActionBtn
                              $color="#3b6fe0"
                              $bg="#e8eefa"
                              onClick={() => { window.location.href = `tel:${contact.telefone}`; }}
                            >
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              Ligar
                            </HeroActionBtn>
                          </HeroActions>
                        </>
                      )}
                    </AvatarHeroWrap>
                  </HeroCardInner>
                </HeroCard>

                {/* ── Creator Pill ── */}
                {(contact as any).assessor && (
                  <CreatorPill>
                    <CreatorAvatar>
                      {(contact as any).assessor.nome.split(' ').map((w: string) => w[0]).slice(0,2).join('')}
                    </CreatorAvatar>
                    <CreatorInfo>
                      <CreatorLabel>Cadastrado por</CreatorLabel>
                      <CreatorName>
                        {(contact as any).assessor.nome.split(' ')[0]}
                        {contact.data_cadastro && (
                          <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 4 }}>
                            · {new Date(contact.data_cadastro + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </CreatorName>
                    </CreatorInfo>
                  </CreatorPill>
                )}

                {/* ── Contato Card ── */}
                <Card>
                  <CardLabel>Contato</CardLabel>

                  <Row onClick={() => canEditContact && openSheet('telefone', contact.telefone || '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Telefone</RowLabel>
                      {contact.telefone
                        ? <RowValue>{formatPhone(contact.telefone)}</RowValue>
                        : <RowEmpty>Não informado</RowEmpty>
                      }
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>

                  <Row onClick={() => canEditContact && openSheet('email', contact.email || '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Email</RowLabel>
                      {contact.email
                        ? <RowValue>{contact.email}</RowValue>
                        : <RowEmpty>Não informado</RowEmpty>
                      }
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>

                  <Row onClick={() => canEditContact && openSheet('nascimento', contact.data_nascimento ? contact.data_nascimento.slice(0, 10) : '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Nascimento</RowLabel>
                      {contact.data_nascimento ? (
                        <RowValue>
                          {fmtDate(contact.data_nascimento)} · {calcAge(contact.data_nascimento)} anos
                          {birthdayToday && <span style={{ marginLeft: 6, color: '#f59e0b' }}>🎉</span>}
                        </RowValue>
                      ) : (
                        <RowEmpty>Não informado</RowEmpty>
                      )}
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>

                  <Row onClick={() => canEditContact && openSheet('cidade', '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Cidade</RowLabel>
                      {contact.cidade
                        ? <RowValue>{contact.cidade.nome} - {contact.cidade.uf}</RowValue>
                        : <RowEmpty>Não informada</RowEmpty>
                      }
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>
                </Card>

                {/* ── Tags ── */}
                <TagsCard>
                  <TagsCardLabel>Tags</TagsCardLabel>
                  <TagSelector
                    selectedTags={selectedTags}
                    onChange={saveContactTags}
                    disabled={!canEditContact}
                  />
                </TagsCard>

                {/* ── Sobre Card ── */}
                <Card>
                  <CardLabel>Sobre</CardLabel>

                  <Row onClick={() => canEditContact && openSheet('profissao', contact.profissao || '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Profissão</RowLabel>
                      {contact.profissao
                        ? <RowValue>{contact.profissao}</RowValue>
                        : <RowEmpty>Não informada</RowEmpty>
                      }
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>

                  <Row onClick={() => canEditContact && openSheet('time', '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Time do coração</RowLabel>
                      {contact.time_coracao
                        ? <RowValue>{contact.time_coracao}</RowValue>
                        : <RowEmpty>Não informado</RowEmpty>
                      }
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>

                  <Row
                    onClick={() => canEditContact && saveField('multiplicador', { multiplicador: !contact.multiplicador })}
                    style={{ cursor: canEditContact ? 'pointer' : 'default' }}
                  >
                    <RowIcon style={{ background: contact.multiplicador ? '#fef3c7' : '#f1f5f9', color: contact.multiplicador ? '#d97706' : '#64748b' }}>
                      <svg fill="currentColor" viewBox="0 0 20 20" width="15" height="15">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Multiplicador</RowLabel>
                      <RowValue>{contact.multiplicador ? 'Sim' : 'Não'}</RowValue>
                    </RowContent>
                    {savingField === 'multiplicador' && <Spinner />}
                  </Row>
                </Card>

                {/* ── Redes Sociais Card ── */}
                <Card>
                  <CardLabel>Redes Sociais</CardLabel>
                  <Row onClick={() => canEditContact && openSheet('rede_social', contact.rede_social || '')}>
                    <RowIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </RowIcon>
                    <RowContent>
                      <RowLabel>Rede Social</RowLabel>
                      {contact.rede_social
                        ? <RowValue>{contact.rede_social}</RowValue>
                        : <RowEmpty>Não informado</RowEmpty>
                      }
                    </RowContent>
                    {canEditContact && <RowChevron><svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg></RowChevron>}
                  </Row>
                </Card>

                {/* ── Notas Internas Card ── */}
                <NotasCard>
                  <CardLabel>Notas Internas</CardLabel>
                  <NotasBody>
                    {contact.observacoes
                      ? <NotasText>{contact.observacoes}</NotasText>
                      : <NotasEmpty>Sem notas internas</NotasEmpty>
                    }
                    {canEditContact && (
                      <NotasEditBtn onClick={() => openSheet('observacoes', contact.observacoes || '')}>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14">
                          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Editar notas
                      </NotasEditBtn>
                    )}
                  </NotasBody>
                </NotasCard>

                {/* ── Activity ── */}
                <ActivityCard style={{ marginBottom: 0 }}>
                  <TabRow>
                    <Tab $a={atab === 'registros'} onClick={() => setAtab('registros')}>
                      Registros
                      {(registros.length + logEntries.length) > 0 && (
                        <TabBadge>{registros.length + logEntries.length}</TabBadge>
                      )}
                    </Tab>
                    <Tab $a={atab === 'demandas'} onClick={() => setAtab('demandas')}>
                      Demandas
                      {demandas.length > 0 && <TabBadge>{demandas.length}</TabBadge>}
                    </Tab>
                  </TabRow>

                  {/* Registros tab */}
                  {atab === 'registros' && (
                    <FeedWrap>
                      {loadingRegistros ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
                          {[1,2,3].map(i => (
                            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                              <ShimmerLine $w={28} $h={28} style={{ borderRadius: '50%', flexShrink: 0 }} />
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <ShimmerLine $w="50%" $h={13} />
                                <ShimmerLine $w="80%" $h={11} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : timelineGroups.length === 0 ? (
                        <Empty>Nenhum registro ainda</Empty>
                      ) : (
                        timelineGroups.map(({ label, items }, gi) => (
                          <React.Fragment key={label}>
                            <DateGroup>{label}</DateGroup>
                            {items.map((entry, idx) => {
                              const isLast = gi === timelineGroups.length - 1 && idx === items.length - 1;
                              const time = new Date(entry.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                              if (entry._source === 'history') {
                                const hist = entry as HistoryItem;
                                return (
                                  <FeedItem key={`log-${hist.id}`}>
                                    <FeedLeft>
                                      <FeedAvatar $type="history">
                                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </FeedAvatar>
                                      {!isLast && <FeedLine />}
                                    </FeedLeft>
                                    <FeedContent>
                                      <FeedMeta>
                                        <FeedAuthor>{hist.registrou?.nome || 'Sistema'}</FeedAuthor>
                                        <FeedAction> {hist.observacao}</FeedAction>
                                        <FeedTime>{time}</FeedTime>
                                      </FeedMeta>
                                    </FeedContent>
                                  </FeedItem>
                                );
                              }

                              const reg = entry as CommentItem;
                              return (
                                <FeedItem key={`reg-${reg.id}`}>
                                  <FeedLeft>
                                    <FeedAvatar $type="comment">
                                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </FeedAvatar>
                                    {!isLast && <FeedLine />}
                                  </FeedLeft>
                                  <FeedContent>
                                    <FeedMeta>
                                      <FeedAuthor>{reg.usuario.nome}</FeedAuthor>
                                      <FeedAction> comentou</FeedAction>
                                      <FeedTime>{time}</FeedTime>
                                    </FeedMeta>
                                    <FeedCard>{reg.descricao}</FeedCard>
                                  </FeedContent>
                                </FeedItem>
                              );
                            })}
                          </React.Fragment>
                        ))
                      )}
                    </FeedWrap>
                  )}

                  {/* Demandas tab */}
                  {atab === 'demandas' && (
                    loadingDemandas ? (
                      <div style={{ padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {[1,2].map(i => (
                          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <ShimmerLine $w="70%" $h={15} />
                            <ShimmerLine $w="30%" $h={11} />
                          </div>
                        ))}
                      </div>
                    ) : demandas.length === 0 ? (
                      <Empty>Nenhuma demanda encontrada</Empty>
                    ) : (
                      demandas.map((d: any) => (
                        <DemandaRow key={d.id} onClick={() => setOpenDemand(d)} style={{ cursor: 'pointer' }}>
                          <DemandaTitle>{d.titulo}</DemandaTitle>
                          <DemandaStatus $status={d.status}>
                            {DemandaStatusLabel[d.status] || d.status}
                          </DemandaStatus>
                          <div style={{ marginLeft: 'auto', color: '#c7c7cc', flexShrink: 0, paddingTop: 2 }}>
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="14" height="14"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                          </div>
                        </DemandaRow>
                      ))
                    )
                  )}
                </ActivityCard>

                <div style={{ height: 20 }} />
              </>
            )}
          </Body>

          {/* ── Composer Bar ── */}
          {!!contact && canCreate && (
            <ComposerBar>
              <ComposerInput
                value={newRegistro}
                onChange={e => setNewRegistro(e.target.value)}
                placeholder="Anote uma conversa, lembrete..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddRegistro(); } }}
                rows={1}
              />
              <ComposerSend
                onClick={handleAddRegistro}
                disabled={!newRegistro.trim() || sendingRegistro}
                aria-label="Enviar"
              >
                {sendingRegistro ? (
                  <Spinner style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} />
                ) : (
                  <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </ComposerSend>
            </ComposerBar>
          )}
        </Sheet>
      </Overlay>

      {/* ── Bottom Sheet overlay ── */}
      <SheetOverlay $open={!!sheetKind} onClick={closeSheet} />

      {/* ── Sheet: text fields ── */}
      {(sheetKind === 'nome' || sheetKind === 'telefone' || sheetKind === 'email' || sheetKind === 'profissao' || sheetKind === 'rede_social') && (
        <BottomSheet $open={!!sheetKind}>
          <SheetHandle />
          <SheetTitle>
            {sheetKind === 'nome' && 'Editar nome'}
            {sheetKind === 'telefone' && 'Telefone'}
            {sheetKind === 'email' && 'Email'}
            {sheetKind === 'profissao' && 'Profissão'}
            {sheetKind === 'rede_social' && 'Rede Social'}
          </SheetTitle>
          <SheetInput
            autoFocus
            type={sheetKind === 'email' ? 'email' : sheetKind === 'telefone' ? 'tel' : 'text'}
            placeholder={
              sheetKind === 'telefone' ? '(00) 00000-0000' :
              sheetKind === 'email' ? 'email@exemplo.com' :
              sheetKind === 'rede_social' ? '@usuario ou link' : ''
            }
            value={sheetValue}
            onChange={e => setSheetValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveSheet(); }}
          />
          <SheetActions>
            <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
            <SheetBtn $primary onClick={saveSheet} disabled={savingSheet}>
              {savingSheet ? 'Salvando...' : 'Salvar'}
            </SheetBtn>
          </SheetActions>
        </BottomSheet>
      )}

      {/* ── Sheet: observacoes ── */}
      {sheetKind === 'observacoes' && (
        <BottomSheet $open>
          <SheetHandle />
          <SheetTitle>Notas internas</SheetTitle>
          <SheetTextArea
            autoFocus
            placeholder="Anotações e observações sobre o contato..."
            value={sheetValue}
            onChange={e => setSheetValue(e.target.value)}
          />
          <SheetActions>
            <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
            <SheetBtn $primary onClick={saveSheet} disabled={savingSheet}>
              {savingSheet ? 'Salvando...' : 'Salvar'}
            </SheetBtn>
          </SheetActions>
        </BottomSheet>
      )}

      {/* ── Sheet: nascimento ── */}
      {sheetKind === 'nascimento' && (
        <BottomSheet $open>
          <SheetHandle />
          <SheetTitle>Data de nascimento</SheetTitle>
          <SheetDateInput
            autoFocus
            type="date"
            value={sheetValue}
            onChange={e => setSheetValue(e.target.value)}
          />
          <SheetActions>
            <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
            <SheetBtn $primary onClick={saveSheet} disabled={savingSheet}>
              {savingSheet ? 'Salvando...' : 'Salvar'}
            </SheetBtn>
          </SheetActions>
        </BottomSheet>
      )}

      {/* ── Sheet: cidade ── */}
      {sheetKind === 'cidade' && (
        <BottomSheet $open>
          <SheetHandle />
          <SheetTitle>Cidade</SheetTitle>
          <SheetInput
            autoFocus
            placeholder="Buscar cidade..."
            value={sheetSearch}
            onChange={e => setSheetSearch(e.target.value)}
          />
          <SheetList>
            {sortedCities
              .filter(c => c.nome.toLowerCase().includes(sheetSearch.toLowerCase()))
              .slice(0, 80)
              .map(c => (
                <SheetOption
                  key={c.id}
                  $active={contact?.cidade_id === c.id}
                  onClick={() => saveSheetCity(c.id)}
                >
                  {c.nome} - {c.microrregiao.mesorregiao.UF.sigla}
                </SheetOption>
              ))}
          </SheetList>
        </BottomSheet>
      )}

      {/* ── Sheet: time do coração ── */}
      {sheetKind === 'time' && (
        <BottomSheet $open>
          <SheetHandle />
          <SheetTitle>Time do coração</SheetTitle>
          <SheetList>
            {timesRS.map(t => (
              <SheetOption
                key={t}
                $active={contact?.time_coracao === t}
                onClick={() => saveSheetTime(t)}
              >
                {t}
              </SheetOption>
            ))}
          </SheetList>
          <SheetActions>
            <SheetBtn onClick={closeSheet}>Cancelar</SheetBtn>
          </SheetActions>
        </BottomSheet>
      )}

      {/* ── Demand detail modal ── */}
      <DemandDetailModal
        isOpen={!!openDemand}
        demand={openDemand}
        onClose={() => setOpenDemand(null)}
        onUpdate={() => {
          if (contact) fetchDemandas(contact.id);
        }}
      />
    </>
  );
};

export default ContactDetailModalMobile;
